import "./FilterMobileStyles.scss";
import { FC, useContext, useEffect, useState } from "react";
import FilterComponentProps from "./FilterComponentProps";
import BrandContext from "../../../../../../../domain/providers/brand/BrandContext";
import BrandContextType from "../../../../../../../domain/providers/brand/BrandContextType";
import ColorContext from "../../../../../../../domain/providers/color/ColorContext";
import ColorContextType from "../../../../../../../domain/providers/color/ColorContextType";
import TypeOfFuelContext from "../../../../../../../domain/providers/typeOfFuel/TypeOfFuelContext";
import TypeOfFuelContextType from "../../../../../../../domain/providers/typeOfFuel/TypeOfFuelContextType";
import TypeVehicleContext from "../../../../../../../domain/providers/typeVehicle/TypeVehicleContext";
import TypeVehicleContextType from "../../../../../../../domain/providers/typeVehicle/TypeVehicleContextType";
import { TraccionCar, TransmissionCar } from "../../../../../../../domain/entities/CarEntity";
import di from "../../../../../../../di/DependencyInjection";
import GetModelsByBrandUseCase, {
    GetModelsByBrandUseCaseName,
} from "../../../../../../../domain/use_cases/brand/GetModelsByBrandUseCase";
import RangePickerComponent from "../../../../../components/form/rangePicker/RangePickerComponent";
import { rangosDePrecioHasta } from "../../../../../components/form/priceRangeChips/PriceRangeChipsComponent";
import Button from "../../../../../components/Button";
import TagContext from "../../../../../../../domain/providers/tag/TagContext";
import TagContextType from "../../../../../../../domain/providers/tag/TagContextType";
import SedeContext from "../../../../../../../domain/providers/sede/SedeContext";
import SedeContextType from "../../../../../../../domain/providers/sede/SedeContextType";
import ModelEntity from "../../../../../../../domain/entities/ModelEntity";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const FilterComponentMobile: FC<FilterComponentProps> = ({
    formFunctions,
    isOpen,
    setIsOpen,
    handleClearFilters,
    onChange,
    ocultarTipoVehiculo,
    filtroFijo,
}) => {
    const toggleArrayValue = (current: string[] | undefined, val: string): string[] => {
        const arr = current ?? [];
        return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
    };
    const { register, setValue, watch } = formFunctions;
    const { brands } = useContext(BrandContext) as BrandContextType;
    const { colors } = useContext(ColorContext) as ColorContextType;
    const { tags } = useContext(TagContext) as TagContextType;
    const { sedes } = useContext(SedeContext) as SedeContextType;
    const { typeOfFuels } = useContext(TypeOfFuelContext) as TypeOfFuelContextType;
    const { typeVehicles } = useContext(TypeVehicleContext) as TypeVehicleContextType;

    const [modelsMap, setModelsMap] = useState<Record<string, ModelEntity[]>>({});
    const [activeTab, setActiveTab] = useState<string>("brand");

    const years: number[] = (() => {
        const y = watch("year");
        if (!y) return [];
        return String(y)
            .split("-")
            .map(Number)
            .filter((n) => !isNaN(n) && n > 0);
    })();

    const brandIds: string[] = (() => {
        const v = watch("brand_id");
        if (Array.isArray(v)) return v.map(String);
        return v ? [String(v)] : [];
    })();
    const modelIds: string[] = (() => {
        const m = watch("model");
        if (Array.isArray(m)) return m.map((id) => String(id));
        if (m?.id !== undefined) return [String(m.id)];
        if (m !== undefined && m !== null) return [String(m)];
        return [];
    })();
    const plateNumberValues: string[] = (() => {
        const v = watch("plate_number");
        if (Array.isArray(v)) return v.map(String);
        return v !== undefined && v !== null && v !== "" ? [String(v)] : [];
    })();
    const typeVehicleIds: string[] = watch("type_vehcile_id") ?? [];
    const typeTransmissions: string[] = watch("type_transmission") ?? [];
    const availabilityIds: string[] = watch("tag_id") ?? [];
    const typeFuelIds: string[] = watch("type_fuel_id") ?? [];
    const colorIds: string[] = Array.isArray(watch("color_id"))
        ? watch("color_id")
        : watch("color_id")
          ? [String(watch("color_id"))]
          : [];

    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    // --- LÓGICA DE HANDLERS (Mantenida de tu código original) ---

    // La marca es de selección única, igual que en escritorio: elegir otra marca reemplaza
    // la anterior en vez de acumularse. Antes se guardaba un arreglo de marcas mientras la URL
    // (?brand=) solo conservaba la última, así que al filtrar Mercedes y luego Kia la búsqueda
    // se hacía con ambas y el listado mezclaba resultados de las dos.
    const _handleChangeBrand = async (brand: any) => {
        const brandIdStr = String(brand.id);
        const isCurrentlySelected = brandIds.includes(brandIdStr);

        setValue("brand_id", isCurrentlySelected ? undefined : brand.id);
        // Los modelos pertenecen a la marca anterior, se limpian en cualquiera de los dos casos.
        setValue("model", undefined);

        setSearchParams(
            (prev) => {
                if (isCurrentlySelected) prev.delete("brand");
                else prev.set("brand", brand.name);
                prev.delete("model");
                return prev;
            },
            { replace: true },
        );

        if (isCurrentlySelected) return;

        try {
            const response = await di.get<GetModelsByBrandUseCase>(GetModelsByBrandUseCaseName).call(brand.id);
            setModelsMap((prev) => ({ ...prev, [brandIdStr]: response }));
        } catch (error) {
            console.error(error);
        }
    };

    const _handleChangeModel = (model: ModelEntity) => {
        const modelIdStr = String(model.id);
        const isSelected = modelIds.includes(modelIdStr);
        const newModelIds = isSelected ? modelIds.filter((id) => id !== modelIdStr) : [...modelIds, modelIdStr];
        setValue("model", newModelIds.length ? newModelIds : undefined);
    };

    const _handleChangeYear = (year: number) => {
        const isSelected = years.includes(year);
        const newYears = isSelected ? years.filter((y) => y !== year) : [...years, year];

        const yearStr = newYears.join("-") || undefined;
        setValue("year", yearStr);

        setSearchParams(
            (prev) => {
                if (yearStr) prev.set("year", yearStr);
                else prev.delete("year");
                return prev;
            },
            { replace: true },
        );
    };

    const handleChangeAddQueryParam = (value: any, propertyForm: string, param: string) => {
        // Solo actualizamos si el valor es diferente para evitar renders innecesarios
        if (watch(propertyForm) == value) return;

        setValue(propertyForm, value);

        setSearchParams(
            (prev) => {
                prev.set(param, value.toString());
                return prev;
            },
            { replace: true },
        );
    };
    // Sync km range to URL (mobile)
    const kmMin = watch("km.min");
    const kmMax = watch("km.max");
    useEffect(() => {
        const url = new URL(window.location.href);
        if (kmMin !== undefined && kmMin !== null && kmMin !== "") {
            url.searchParams.set("km_min", String(kmMin));
        } else {
            url.searchParams.delete("km_min");
        }
        if (kmMax !== undefined && kmMax !== null && kmMax !== "") {
            url.searchParams.set("km_max", String(kmMax));
        } else {
            url.searchParams.delete("km_max");
        }
        window.history.replaceState({}, "", url.toString());
    }, [kmMin, kmMax]);

    // Read ALL query params on mount and populate form (mobile)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        const typeVehicleParam = params.get("type_vehicle");
        if (typeVehicleParam) {
            const parts = typeVehicleParam.split("-").filter(Boolean);
            const allNumeric = parts.every((p) => /^\d+$/.test(p));
            if (allNumeric) {
                setValue("type_vehcile_id", parts);
            } else {
                const found = typeVehicles.find((tv) => tv.name === typeVehicleParam);
                if (found) setValue("type_vehcile_id", [String(found.id)]);
            }
        }
        const transmissionParam = params.get("transmission");
        if (transmissionParam) {
            setValue("type_transmission", transmissionParam.split("-").filter(Boolean));
        }
        const traccionParam = params.get("traccion");
        if (traccionParam) {
            setValue("traccion", traccionParam.split("-").filter(Boolean));
        }
        const tagParam = params.get("tag");
        if (tagParam) {
            setValue("tag_id", tagParam.split("-").filter(Boolean));
        }
        const fuelParam = params.get("typeOfFuels");
        if (fuelParam) {
            setValue("type_fuel_id", fuelParam.split("-").filter(Boolean));
        }
        const colorParam = params.get("color");
        if (colorParam) {
            setValue("color_id", colorParam.split("-").filter(Boolean));
        }
        const plateParam = params.get("plate");
        if (plateParam) {
            setValue("plate_number", plateParam.split("-").filter(Boolean));
        }
        const yearParam = params.get("year");
        if (yearParam) setValue("year", yearParam);
        const minPrice = params.get("min_price");
        const maxPrice = params.get("max_price");
        if (minPrice) setValue("price.min", Number(minPrice));
        if (maxPrice) setValue("price.max", Number(maxPrice));
        const kmMinParam = params.get("km_min");
        const kmMaxParam = params.get("km_max");
        if (kmMinParam) setValue("km.min", Number(kmMinParam));
        if (kmMaxParam) setValue("km.max", Number(kmMaxParam));
        const locationParam = params.get("location");
        if (locationParam) {
            setValue("location", locationParam.split("-").filter(Boolean));
        }
    }, []);

    // --- RENDERIZADO DE CONTENIDO DERECHO ---

    const renderContent = () => {
        switch (activeTab) {
            case "brand":
                return (
                    <div className="filter_content_pane">
                        <h4 className="section_title">Marca y modelos</h4>
                        <div className="brand_grid">
                            {brands.map((brand) => {
                                const brandIdStr = String(brand.id);
                                const isBrandSelected = brandIds.includes(brandIdStr);
                                return (
                                    <div key={brand.id} className="brand_wrapper">
                                        <div
                                            className={`brand_chip ${isBrandSelected ? "active" : ""}`}
                                            onClick={() => _handleChangeBrand(brand)}
                                        >
                                            <img src={brand.image} alt={brand.name} />
                                            <span>{brand.name}</span>
                                        </div>

                                        {isBrandSelected && (modelsMap[brandIdStr]?.length ?? 0) > 0 && (
                                            <div className="models_container_inline">
                                                {(modelsMap[brandIdStr] ?? [])
                                                    .filter((m) => m.id !== "163")
                                                    .map((model) => (
                                                        <div
                                                            key={model.id}
                                                            className={`model_tag ${
                                                                modelIds.includes(String(model.id)) ? "active" : ""
                                                            }`}
                                                            onClick={() => _handleChangeModel(model)}
                                                        >
                                                            {model.name}
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );

            case "year":
                return (
                    <div className="filter_content_pane">
                        <h4 className="section_title">Año</h4>
                        {years.length > 0 && (
                            <div className="selected_years_summary">{years.sort((a, b) => a - b).join(", ")}</div>
                        )}
                        <div className="year_grid">
                            {Array.from(
                                { length: new Date().getFullYear() - Math.max(filtroFijo?.anioMinimo ?? 0, 2011) + 1 },
                                (_, i) => Math.max(filtroFijo?.anioMinimo ?? 0, 2011) + i,
                            )
                                .reverse()
                                .map((year) => (
                                    <div
                                        key={year}
                                        className={`year_chip ${years.includes(year) ? "active" : ""}`}
                                        onClick={() => _handleChangeYear(year)}
                                    >
                                        {year}
                                    </div>
                                ))}
                        </div>
                    </div>
                );

            case "price":
                const currentPrice = watch("price") || { min: 0, max: 1000000000 };

                const formatMoney = (value: number) =>
                    new Intl.NumberFormat("es-CO", {
                        style: "currency",
                        currency: "COP",
                        maximumFractionDigits: 0,
                    }).format(value);

                const parsePriceInput = (raw: string): number => {
                    const cleaned = raw.replace(/[^0-9]/g, "");
                    return cleaned === "" ? 0 : parseInt(cleaned, 10);
                };

                const formatInputDisplay = (raw: string): string => {
                    const num = parsePriceInput(raw);
                    if (num === 0 && raw === "") return "";
                    return "$ " + num.toLocaleString("es-CO");
                };

                const handlePriceInputChange = (type: "min" | "max", raw: string) => {
                    const num = parsePriceInput(raw);
                    const clamped = Math.min(Math.max(num, 0), 1000000000);
                    setValue(`price.${type}`, clamped);
                    setSearchParams(
                        (prev) => {
                            prev.set(type === "min" ? "min_price" : "max_price", clamped.toString());
                            return prev;
                        },
                        { replace: true },
                    );
                };

                const quickRanges = rangosDePrecioHasta(filtroFijo?.precioMaximo);

                const handleQuickRange = (min: number, max: number) => {
                    setValue("price", { min, max });
                    setSearchParams(
                        (prev) => {
                            prev.set("min_price", min.toString());
                            prev.set("max_price", max.toString());
                            return prev;
                        },
                        { replace: true },
                    );
                };

                return (
                    <div className="filter_content_pane">
                        <h4 className="section_title">Precio</h4>

                        <div className="price_filter_container">
                            <RangePickerComponent
                                hiddenInputs={true}
                                formFunctions={formFunctions}
                                keyName="price"
                                min={0}
                                max={1000000000}
                            />

                            <div className="price_inputs_row">
                                <input
                                    className="price_input_box"
                                    inputMode="numeric"
                                    placeholder="Mínimo"
                                    value={formatInputDisplay((currentPrice.min ?? 0).toString())}
                                    onChange={(e) => handlePriceInputChange("min", e.target.value)}
                                    onBlur={(e) => {
                                        const num = parsePriceInput(e.target.value);
                                        const max = currentPrice.max ?? 1000000000;
                                        if (num > max) handlePriceInputChange("min", max.toString());
                                    }}
                                />
                                <input
                                    className="price_input_box"
                                    inputMode="numeric"
                                    placeholder="Máximo"
                                    value={formatInputDisplay((currentPrice.max ?? 1000000000).toString())}
                                    onChange={(e) => handlePriceInputChange("max", e.target.value)}
                                    onBlur={(e) => {
                                        const num = parsePriceInput(e.target.value);
                                        const min = currentPrice.min ?? 0;
                                        if (num < min) handlePriceInputChange("max", min.toString());
                                    }}
                                />
                            </div>

                            <div className="quick_ranges_grid">
                                {quickRanges.map((range, idx) => (
                                    <div
                                        key={idx}
                                        className={`range_chip ${
                                            currentPrice.min === range.min && currentPrice.max === range.max
                                                ? "active"
                                                : ""
                                        }`}
                                        onClick={() => handleQuickRange(range.min, range.max)}
                                    >
                                        {range.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case "type":
                return (
                    <div className="filter_content_pane">
                        <h4 className="section_title">Tipo de Vehículo</h4>
                        {typeVehicles
                            .filter((type) => !filtroFijo?.tiposExcluidos?.includes(String(type.id)))
                            .map((type, index) => {
                                const isSelected = typeVehicleIds.includes(String(type.id));
                                return (
                                    <div
                                        key={index}
                                        className="custom_radio_option"
                                        onClick={() => {
                                            const newVal = toggleArrayValue(typeVehicleIds, String(type.id));
                                            setValue("type_vehcile_id", newVal);
                                            setSearchParams(
                                                (prev) => {
                                                    if (newVal.length) prev.set("type_vehicle", newVal.join("-"));
                                                    else prev.delete("type_vehicle");
                                                    return prev;
                                                },
                                                { replace: true },
                                            );
                                        }}
                                    >
                                        <div className={`radio_circle ${isSelected ? "selected" : ""}`}></div>
                                        <img src={type.photo} width={25} alt="" className="mx-2" />
                                        <label>
                                            {type.name} <small className="text-muted">({type.count ?? 0})</small>
                                        </label>
                                    </div>
                                );
                            })}
                    </div>
                );
            case "traction": {
                const rawTraction = watch("traccion");
                const tractionIds: string[] = Array.isArray(rawTraction)
                    ? rawTraction
                    : rawTraction
                      ? [String(rawTraction)]
                      : [];
                return (
                    <div className="filter_content_pane">
                        <h4 className="section_title">Tracción</h4>
                        {[
                            { label: "Delantera", value: "2" },
                            { label: "Trasera", value: "0" },
                            { label: "4 x 4", value: "1" },
                            { label: "4 x 2", value: "3" },
                        ].map((item, index) => {
                            const isSelected = tractionIds.includes(item.value);
                            return (
                                <div
                                    key={index}
                                    className="custom_radio_option"
                                    onClick={() => {
                                        const newVal = toggleArrayValue(tractionIds, item.value);
                                        setValue("traccion", newVal);
                                        setSearchParams(
                                            (prev) => {
                                                if (newVal.length) prev.set("traccion", newVal.join("-"));
                                                else prev.delete("traccion");
                                                return prev;
                                            },
                                            { replace: true },
                                        );
                                    }}
                                >
                                    <div className={`radio_circle ${isSelected ? "selected" : ""}`}></div>
                                    <label className="ms-2">{item.label}</label>
                                </div>
                            );
                        })}
                    </div>
                );
            }

            case "trans":
                return (
                    <div className="filter_content_pane">
                        <h4 className="section_title">Transmisión</h4>
                        {[
                            { label: "Automática", value: TransmissionCar.AUTOMATIC },
                            { label: "Manual", value: TransmissionCar.MANUAL },
                        ].map((item, index) => {
                            const isSelected = typeTransmissions.includes(item.value);
                            return (
                                <div
                                    key={index}
                                    className="custom_radio_option"
                                    onClick={() => {
                                        const newVal = toggleArrayValue(typeTransmissions, item.value);
                                        setValue("type_transmission", newVal);
                                        setSearchParams(
                                            (prev) => {
                                                if (newVal.length) prev.set("transmission", newVal.join("-"));
                                                else prev.delete("transmission");
                                                return prev;
                                            },
                                            { replace: true },
                                        );
                                    }}
                                >
                                    <div className={`radio_circle ${isSelected ? "selected" : ""}`}></div>
                                    <label className="ms-2">{item.label}</label>
                                </div>
                            );
                        })}
                    </div>
                );

            case "disp":
                return (
                    <div className="filter_content_pane">
                        <h4 className="section_title">Disponibilidad</h4>
                        {tags
                            .filter((t) => t.id !== "9" && t.id !== "13")
                            .map((tag, index) => {
                                const isSelected = availabilityIds.includes(String(tag.id));
                                return (
                                    <div
                                        key={index}
                                        className="custom_radio_option"
                                        onClick={() => {
                                            const newVal = toggleArrayValue(availabilityIds, String(tag.id));
                                            setValue("tag_id", newVal);
                                            setSearchParams(
                                                (prev) => {
                                                    if (newVal.length) prev.set("tag", newVal.join("-"));
                                                    else prev.delete("tag");
                                                    return prev;
                                                },
                                                { replace: true },
                                            );
                                        }}
                                    >
                                        <div className={`radio_circle ${isSelected ? "selected" : ""}`}></div>
                                        <label className="ms-2">{tag.name}</label>
                                    </div>
                                );
                            })}
                    </div>
                );
            case "km":
                return (
                    <div className="filter_content_pane">
                        <h4 className="section_title">Kilometraje</h4>
                        <RangePickerComponent formFunctions={formFunctions} keyName="km" min={0} max={1000000} />
                    </div>
                );

            case "fuel":
                return (
                    <div className="filter_content_pane">
                        <h4 className="section_title">Combustible</h4>
                        {typeOfFuels.map((fuel, index) => {
                            const isSelected = typeFuelIds.includes(String(fuel.id));
                            return (
                                <div
                                    key={index}
                                    className="custom_radio_option"
                                    onClick={() => {
                                        const newVal = toggleArrayValue(typeFuelIds, String(fuel.id));
                                        setValue("type_fuel_id", newVal);
                                        setSearchParams(
                                            (prev) => {
                                                if (newVal.length) prev.set("typeOfFuels", newVal.join("-"));
                                                else prev.delete("typeOfFuels");
                                                return prev;
                                            },
                                            { replace: true },
                                        );
                                    }}
                                >
                                    <div className={`radio_circle ${isSelected ? "selected" : ""}`}></div>
                                    <label className="ms-2">{fuel.name}</label>
                                </div>
                            );
                        })}
                    </div>
                );
            case "color":
                return (
                    <div className="filter_content_pane">
                        <h4 className="section_title">Color Exterior</h4>
                        <div className="filter_section_wrapper">
                            <div className="colors_grid_layout">
                                {colors.map((color, index) => {
                                    const isSelected = colorIds.includes(color.name);
                                    return (
                                        <div
                                            key={index}
                                            className={`color_option ${isSelected ? "active" : ""}`}
                                            onClick={() => {
                                                const newVal = toggleArrayValue(colorIds, color.name);
                                                setValue("color_id", newVal);
                                                setSearchParams(
                                                    (prev) => {
                                                        if (newVal.length) prev.set("color", newVal.join("-"));
                                                        else prev.delete("color");
                                                        return prev;
                                                    },
                                                    { replace: true },
                                                );
                                            }}
                                        >
                                            <div
                                                className={`color_circle_display ${
                                                    color.name.toLowerCase().includes("blanco") ? "white_border" : ""
                                                }`}
                                                style={{ backgroundColor: color.colorHex }}
                                            />
                                            <span>{color.name}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                );

            case "location":
                return (
                    <div className="filter_content_pane">
                        <h4 className="section_title">Sede</h4>
                        {sedes.map((sede, index) => {
                            const current: string[] = watch("location") || [];
                            const isSelected = current.includes(String(sede.id));
                            return (
                                <div
                                    key={index}
                                    className="custom_radio_option"
                                    onClick={() => {
                                        const next = isSelected
                                            ? current.filter((l) => l !== String(sede.id))
                                            : [...current, String(sede.id)];
                                        setValue("location", next);
                                        setSearchParams(
                                            (prev) => {
                                                if (next.length) prev.set("location", next.join("-"));
                                                else prev.delete("location");
                                                return prev;
                                            },
                                            { replace: true },
                                        );
                                        onChange?.();
                                    }}
                                >
                                    <div className={`radio_circle ${isSelected ? "selected" : ""}`}></div>
                                    <label className="ms-2">{sede.name}</label>
                                </div>
                            );
                        })}
                    </div>
                );

            case "plate":
                return (
                    <div className="filter_content_pane">
                        <h4 className="section_title">Último dígito de placa</h4>
                        <div className="filter_section_wrapper">
                            <div className="plate_numbers_grid">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((num) => {
                                    const isSelected = plateNumberValues.includes(String(num));

                                    return (
                                        <div
                                            key={num}
                                            className={`plate_number_item ${isSelected ? "active" : ""}`}
                                            onClick={() => {
                                                const newVal = toggleArrayValue(plateNumberValues, String(num));
                                                setValue("plate_number", newVal.length ? newVal : undefined);

                                                setSearchParams(
                                                    (prev) => {
                                                        if (newVal.length) prev.set("plate", newVal.join("-"));
                                                        else prev.delete("plate");
                                                        return prev;
                                                    },
                                                    { replace: true },
                                                );
                                            }}
                                        >
                                            {num}
                                        </div>
                                    );
                                })}
                            </div>
                            <small className="text-muted text-center mt-2">
                                Filtra vehículos según su restricción de movilidad (Pico y Placa).
                            </small>
                        </div>
                    </div>
                );
            default:
                return <div className="filter_content_pane">Selecciona una categoría</div>;
        }
    };

    return (
        <>
            <div className={`filter_backdrop ${"show"}`} onClick={() => setIsOpen(false)}></div>
            <div className={`filter_mobile_drawer ${"open"}`} onClick={(e) => e.stopPropagation()}>
                <div className="drawer_header">
                    <button
                        className="button_clear_filters"
                        onClick={() => {
                            handleClearFilters();

                            setIsOpen(false);
                        }}
                    >
                        Limpiar filtros
                    </button>
                    <span>Filtros</span>
                    <button className="close_x" onClick={() => setIsOpen(false)}>
                        ✕
                    </button>
                </div>

                <div className="drawer_body">
                    {/* Sidebar Izquierdo */}
                    <div className="filter_sidebar">
                        <div
                            className={`sidebar_item ${activeTab == "location" ? "active" : ""}`}
                            onClick={() => setActiveTab("location")}
                        >
                            Ubicación
                        </div>
                        <div
                            className={`sidebar_item ${activeTab == "brand" ? "active" : ""}`}
                            onClick={() => setActiveTab("brand")}
                        >
                            Marca y modelo
                        </div>
                        <div
                            className={`sidebar_item ${activeTab == "year" ? "active" : ""}`}
                            onClick={() => setActiveTab("year")}
                        >
                            Año
                        </div>
                        <div
                            className={`sidebar_item ${activeTab == "price" ? "active" : ""}`}
                            onClick={() => setActiveTab("price")}
                        >
                            Precio
                        </div>
                        {!ocultarTipoVehiculo && (
                            <div
                                className={`sidebar_item ${activeTab == "type" ? "active" : ""}`}
                                onClick={() => setActiveTab("type")}
                            >
                                Tipo
                            </div>
                        )}
                        <div
                            className={`sidebar_item ${activeTab == "trans" ? "active" : ""}`}
                            onClick={() => setActiveTab("trans")}
                        >
                            Transmisión
                        </div>
                        <div
                            className={`sidebar_item ${activeTab == "traction" ? "active" : ""}`}
                            onClick={() => setActiveTab("traction")}
                        >
                            Tracción
                        </div>
                        <div
                            className={`sidebar_item ${activeTab == "disp" ? "active" : ""}`}
                            onClick={() => setActiveTab("disp")}
                        >
                            Disponibilidad
                        </div>
                        <div
                            className={`sidebar_item ${activeTab == "km" ? "active" : ""}`}
                            onClick={() => setActiveTab("km")}
                        >
                            Kilometraje
                        </div>
                        <div
                            className={`sidebar_item ${activeTab == "fuel" ? "active" : ""}`}
                            onClick={() => setActiveTab("fuel")}
                        >
                            Combustible
                        </div>
                        <div
                            className={`sidebar_item ${activeTab == "plate" ? "active" : ""}`}
                            onClick={() => setActiveTab("plate")}
                        >
                            Placa
                        </div>
                        <div
                            className={`sidebar_item ${activeTab == "color" ? "active" : ""}`}
                            onClick={() => setActiveTab("color")}
                        >
                            Color
                        </div>
                    </div>

                    {/* Contenido Derecho */}
                    <div className="filter_main_content">{renderContent()}</div>
                </div>

                {/* Footer con botones */}
                <div className="drawer_footer">
                    <div className="footer_actions">
                        <div className="btn_apply_wrapper">
                            <Button
                                label="Aplicar filtros"
                                variant="primary"
                                onClick={() => setIsOpen(false)}
                                width="100%"
                            />
                        </div>
                        <button
                            className="btn_scroll_top"
                            onClick={() => {
                                const content = document.querySelector(".filter_main_content");
                                content?.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                        >
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 19V5M5 12l7-7 7 7" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FilterComponentMobile;
